import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthContext } from '../../authContext';
import AlertsCustom from '../Alerts';
import { translateBackendError } from '../../utils/translateBackendError';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import SkillNameConflictDialog from './SkillNameConflictDialog';
import ShareSkillModal from '../ShareSkillModal';
import SkillCard from './SkillCard';
import ActionCard from './ActionCard';
import SkillDetailView from './SkillDetailView';
import {
    SKILL_MAX_NAME_CHARS,
    SKILL_MAX_DESCRIPTION_CHARS,
    UnsupportedSkillFileError,
    getSkillFileSizes,
    extractSkillFileName,
    extractSkillFileDescription,
} from './skillUpload';
import './SkillsManagerModal.css';

// Tarefa 4.1 (SC-005): apos confirmar uma mudanca de visibilidade, reflete o
// resultado nas abas ja carregadas (cache por aba, Tarefa 3.1) sem precisar
// de reload/nova busca - remove a skill de toda aba onde ela aparecia e
// reinsere em "minhas" (sempre, ja que so o dono pode compartilhar) e em
// "tpf" quando a nova visibilidade for publica.
function applyVisibilityUpdate(skillsByTab, updatedSkill) {
    const next = {};
    for (const [tab, list] of Object.entries(skillsByTab)) {
        next[tab] = list.filter((s) => s.skill_id !== updatedSkill.skill_id);
    }
    if (next.minhas !== undefined) {
        next.minhas = [updatedSkill, ...next.minhas];
    }
    if (updatedSkill.visibility === 'public' && next.tpf !== undefined) {
        next.tpf = [updatedSkill, ...next.tpf];
    }
    return next;
}

// Filtra pelo termo digitado na busca (FR-012) - nome e descricao, sem
// distincao de maiusculas/minusculas.
function matchesSearch(skill, term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return true;
    return (
        skill.name.toLowerCase().includes(normalized)
        || skill.description.toLowerCase().includes(normalized)
    );
}

// `[REVISADO em 2026-08-12]`: ordem por nível de acesso (mais restrito ao
// mais aberto) - Minhas (só o dono) -> Compartilhadas (alvo especifico) ->
// TPF (todo mundo) - mesmo padrão do Google Drive ("Meu Drive" ->
// "Compartilhados comigo" -> ...). Antes era TPF/Minhas/Compartilhadas.
const TABS = [
    { id: 'minhas', labelKey: 'skills_manager_tab_minhas' },
    { id: 'compartilhadas', labelKey: 'skills_manager_tab_compartilhadas' },
    { id: 'tpf', labelKey: 'skills_manager_tab_tpf' },
];

function SkillsManagerModal({ isOpen, onClose, email, onCreateWithIris = () => { }, onSelectSkill = () => { }, onEditSkillInChat = () => { } }) {
    const { t } = useTranslation('chat');
    const { accessToken } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState('minhas');
    const [skillsByTab, setSkillsByTab] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // FR-078 (Tarefa 11.5): erro/timeout de GET /get_skills por aba - por
    // aba (nao um booleano unico), pra falha numa aba nao contaminar as
    // outras ja carregadas com sucesso (skillsByTab, cache existente da
    // Tarefa 3.1).
    const [skillsErrorByTab, setSkillsErrorByTab] = useState({});

    const addSkillInputRef = useRef(null);
    const [addingSkill, setAddingSkill] = useState(false);
    const [alert, setAlert] = useState(null);
    // Tarefa 7.5 (FR-050), reescrito: upload parado num conflito de nome -
    // { file, name, owned_by_user, skill_id }. Nomes de skill sao unicos na
    // plataforma inteira, entao o popup (SkillNameConflictDialog) oferece as
    // duas saidas possiveis: substituir (so' quando a skill que ocupa o nome
    // e' do proprio usuario) ou criar uma nova com outro nome. Antes daqui,
    // o caso "nome de outra pessoa" nao tinha saida nenhuma - virava um
    // alerta de erro e o upload morria ali.
    const [nameConflict, setNameConflict] = useState(null);
    // Segundo conflito (o nome escolhido no campo tambem ja existe): mantem
    // o popup aberto com o aviso, em vez de fechar e virar alerta.
    const [nameConflictError, setNameConflictError] = useState('');
    const [shareSkillTarget, setShareSkillTarget] = useState(null);
    // Tarefa 6.1 (FR-032/FR-033, PBI 877): skill cujo icone de editar foi
    // clicado - quando definida, substitui o corpo inteiro do modal (header/
    // toolbar/grade) pela tela "Detalhe da Skill", mesmo card/posicao do
    // Figma (node 101:600), em vez de abrir um modal novo em cima do
    // existente. null volta a mostrar a grade, na mesma aba/busca de antes.
    const [detailSkill, setDetailSkill] = useState(null);

    // Tarefa 3.1 (FR-010/FR-011): busca a listagem ao abrir o modal e a cada
    // troca de aba, mas so quando aquela aba ainda nao foi carregada - o
    // resultado e cacheado em skillsByTab por aba (decisao do usuario), sem
    // repetir requisicao ao voltar para uma aba ja buscada. `[REVISADO na
    // Tarefa 11.5, FR-078]`: falha/timeout deixa de ser tratada como lista
    // vazia - marca `skillsErrorByTab[tab]` e NAO grava `skillsByTab[tab]`
    // (fica `undefined`), pra nao fingir que a aba foi carregada com sucesso
    // (sem isso, o card "tentar de novo" nunca reapareceria numa nova
    // abertura do modal). `loadTab` e reutilizavel pelo `useEffect` abaixo
    // (abrir modal/trocar de aba) e pelo botao "tentar de novo" (chamada
    // manual, sem depender de mudar `isOpen`/`activeTab`).
    const loadTab = useCallback((tab) => {
        let cancelled = false;
        setLoading(true);
        setSkillsErrorByTab((prev) => ({ ...prev, [tab]: false }));

        fetch(`${process.env.REACT_APP_API_URL}/get_skills?email=${encodeURIComponent(email)}&tab=${tab}`, {
            headers: { "Authorization": `Bearer ${accessToken}` },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`GET /get_skills falhou (${response.status})`);
                return response.json();
            })
            .then((data) => {
                if (cancelled) return;
                setSkillsByTab((prev) => ({ ...prev, [tab]: data.skills || [] }));
            })
            .catch(() => {
                if (cancelled) return;
                setSkillsErrorByTab((prev) => ({ ...prev, [tab]: true }));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, accessToken]);

    useEffect(() => {
        if (!isOpen) return;
        if (skillsByTab[activeTab] !== undefined) return;
        // Nao tenta de novo so por trocar de aba - a aba com erro so busca
        // de novo via o botao "tentar de novo" (acao explicita do usuario).
        if (skillsErrorByTab[activeTab]) return;

        return loadTab(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, activeTab]);

    const handleRetryTab = () => loadTab(activeTab);

    // Reseta o estado (aba, cache, busca) ao fechar, para a proxima abertura
    // comecar do zero (aba "Minhas" padrao, sem dados de uma sessao anterior).
    useEffect(() => {
        if (!isOpen) {
            setActiveTab('minhas');
            setSkillsByTab({});
            setSkillsErrorByTab({});
            setSearchTerm('');
            setAlert(null);
            setShareSkillTarget(null);
            setDetailSkill(null);
        }
    }, [isOpen]);

    // Achado de acessibilidade (specs/skills/plan.md): nenhum modal de Skills respondia ao
    // Esc - hook precisa vir ANTES do early return abaixo (regra dos hooks).
    useEscapeToClose(onClose, isOpen);

    if (!isOpen) return null;

    const skills = skillsByTab[activeTab] || [];
    const filteredSkills = skills.filter((skill) => matchesSearch(skill, searchTerm));

    // Tarefa 3.4 (FR-014): dispara o mesmo fluxo de criacao via chat da
    // Tarefa 1.1 ("/skill-creator" + texto de ajuda) e fecha o modal, ja que
    // o resultado passa a acontecer no chat por tras dele.
    const handleCreateWithIris = () => {
        onCreateWithIris();
        onClose();
    };

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    // Tarefa 5.1 (FR-023): clicar num SkillCard (qualquer aba) insere a skill
    // no campo de mensagem do chat (via onSelectSkill, resolvido pelo pai
    // comum de Historico/UnifiedChat) e fecha o modal - mesmo padrao ja usado
    // por "Criar com Iris" (Tarefa 3.4), decisao confirmada com o usuario ao
    // iniciar esta tarefa.
    const handleSelectSkill = (skill) => {
        onSelectSkill(skill);
        onClose();
    };

    // Tarefa 6.4 (FR-038): "Editar skill no chat" no menu de opcoes da tela
    // "Detalhe da Skill" - dispara onEditSkillInChat (resolvido pelo pai comum
    // de Historico/UnifiedChat, mesmo mecanismo de sinal { skill, seq } ja
    // usado por onSelectSkill/handleSelectSkill acima, so que carregando o
    // texto de ajuda de edicao em vez do de "Experimentar no chat") e fecha o
    // modal inteiro, mesmo padrao de handleSelectSkill/handleCreateWithIris.
    const handleEditSkill = (skill) => {
        onEditSkillInChat(skill);
        onClose();
    };

    // Tarefa 5.3 (FR-025): baixa o conteudo da skill (icone de download do
    // SkillCard) via GET /get_skill_download e dispara o download no
    // navegador via blob - mesmo padrao ja usado em
    // pages/Chat/DatabaseUptdate/FileList. O backend decide o formato
    // (SKILL.md puro ou .zip com o manifesto completo, Content-Type
    // "application/zip" nesse caso) - o frontend so precisa da extensao pra
    // nomear o arquivo, lida do Content-Type (safelisted em CORS, ao
    // contrario de Content-Disposition) em vez de assumir sempre ".md".
    // `[REVISADO nesta tarefa]`: o icone de download saiu do SkillCard - esta
    // funcao passou a ser usada pelo botao "Baixar" do cabecalho de "Detalhe
    // da Skill" (SkillDetailView.js, skill-detail-download-button).
    const handleDownloadSkill = async (skill) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/get_skill_download?email=${encodeURIComponent(email)}&skill_id=${encodeURIComponent(skill.skill_id)}`,
                { headers: { "Authorization": `Bearer ${accessToken}` } },
            );
            if (!response.ok) {
                throw new Error('download failed');
            }

            const contentType = response.headers.get('Content-Type') || '';
            const extension = contentType.includes('zip') ? '.zip' : '.md';

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${skill.name}${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setAlert({
                type: 'error',
                title: t('skills_manager_download_error_title'),
                message: t('skills_manager_download_error_message'),
            });
        }
    };

    // Tarefa 4.1 (FR-015/016/017): abre o modal "Compartilhar skill" para o
    // card clicado; ao confirmar, atualiza o cache por aba (SC-005) em vez
    // de refazer a busca. Chamado duas vezes por confirmacao (Tarefa 5.10):
    // otimista, no instante do clique, e de novo com o resultado real do
    // POST /post_skill_visibility para reconciliar.
    const handleShareUpdated = (updatedSkill) => {
        setSkillsByTab((prev) => applyVisibilityUpdate(prev, updatedSkill));
    };

    // Tarefa 6.6 (FR-043, revisado em 2026-08-12): o toggle ativar/desativar
    // vive no cabecalho de "Detalhe da Skill" (SkillDetailView.js), acionavel
    // por qualquer usuario com acesso - nao so o dono, que e o unico que
    // chegava la antes (ver getCardOnSelect abaixo). Reconciliacao do cache
    // por aba quando o toggle muda de dentro do Detalhe, mesmo padrao (por
    // skill_id, nao por aba) de applyVisibilityUpdate.
    const handleSkillActiveChanged = (skillId, active) => {
        setSkillsByTab((prev) => {
            const next = {};
            for (const [tab, list] of Object.entries(prev)) {
                next[tab] = list.map((s) => (s.skill_id === skillId ? { ...s, active } : s));
            }
            return next;
        });
    };

    // Tarefa 7.3 (FR-048): edicao direta de nome/descricao/conteudo pelo
    // popup da tela "Detalhe da Skill" - reconcilia o cache por aba (nome/
    // descricao podem ter mudado, refletindo no SkillCard sem reload) e o
    // proprio `detailSkill` (o cabecalho de SkillDetailView le nome/descricao
    // direto do prop `skill`, nao de estado local - precisa da referencia
    // nova para re-renderizar com o nome atualizado). `updatedSkill` vem no
    // mesmo formato de GET /get_skill (com content/files a mais, ignorados
    // pelo SkillCard) - mesmo padrao (por skill_id, nao por aba) de
    // handleSkillActiveChanged/applyVisibilityUpdate.
    const handleSkillEdited = (updatedSkill) => {
        setSkillsByTab((prev) => {
            const next = {};
            for (const [tab, list] of Object.entries(prev)) {
                next[tab] = list.map((s) => (s.skill_id === updatedSkill.skill_id ? { ...s, ...updatedSkill } : s));
            }
            return next;
        });
        setDetailSkill((prev) => (
            prev && prev.skill_id === updatedSkill.skill_id ? { ...prev, ...updatedSkill } : prev
        ));
    };

    // "Excluir" (menu de opcoes da tela "Detalhe da Skill", renomeado de
    // "Desinstalar") - decisao do usuario: o retorno visual (sumir da grade
    // e fechar o Detalhe) NAO espera a resposta do POST /delete_skill. Este
    // callback (chamado por SkillDetailView.js::handleConfirmDelete assim
    // que o popup de confirmacao e confirmado) ja reconcilia o cache local
    // (remove de toda aba, mesmo padrao por skill_id de
    // handleSkillActiveChanged/handleSkillEdited acima) e fecha o Detalhe
    // ANTES de disparar a chamada de rede - fica aqui, nao em
    // SkillDetailView.js, porque aquela tela e desmontada no mesmo instante
    // (o alerta de erro, se houver, precisa sobreviver ao fechamento).
    // Mesmo trade-off ja aceito por handleSaveEdit (SkillDetailView.js) e
    // ShareSkillModal: se a exclusao falhar de verdade no backend, so um
    // alerta avisa - sem trazer o card de volta sozinho, o usuario reabre o
    // "Gerenciador de Skills" (refaz a busca, Tarefa 3.1) se notar o erro.
    const handleSkillDeleted = (skillId) => {
        setSkillsByTab((prev) => {
            const next = {};
            for (const [tab, list] of Object.entries(prev)) {
                next[tab] = list.filter((s) => s.skill_id !== skillId);
            }
            return next;
        });
        setDetailSkill(null);

        fetch(`${process.env.REACT_APP_API_URL}/delete_skill`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, skill_id: skillId }),
        })
            .then(async (response) => {
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(translateBackendError(data.error) || t('skills_detail_delete_error_title'));
                }
            })
            .catch((error) => {
                setAlert({
                    type: 'error',
                    title: t('skills_detail_delete_error_title'),
                    message: error.message || t('skills_detail_delete_error_title'),
                });
            });
    };

    // `[REVISADO em 2026-08-12, Tarefa 6.6, 3a rodada]`: clicar no corpo do
    // card sempre abre "Detalhe da Skill" agora, pra qualquer papel (dono ou
    // nao) - decisao do usuario, pra unificar o comportamento independente
    // de quem esta vendo. handleSelectSkill (insere no chat, FR-023) so e
    // chamado de dentro do Detalhe agora ("Experimentar no chat",
    // onTryInChat abaixo) - nao mais direto do clique no card.

    // POST /post_skill em si (Tarefa 3.3) - extraido a parte para ser
    // chamado tanto direto (nome inedito) quanto pelas duas saidas do popup
    // de conflito de nome: `substituir` (nova versao da propria skill, o que
    // create_skill fazia sozinho antes) e `novoNome` (skill nova, com o
    // SKILL.md reescrito no backend por renomear_skill_md).
    //
    // Conflito (409) NAO vira alerta: devolve o bloco `name_conflict` do
    // backend pro chamador abrir/manter o popup, que e' o unico lugar onde o
    // usuario consegue decidir o que fazer.
    const submitSkillFile = async (file, { novoNome = null, substituir = false } = {}) => {
        const formData = new FormData();
        formData.append('email', email);
        formData.append('file', file);
        if (novoNome) formData.append('new_name', novoNome);
        if (substituir) formData.append('substituir', 'true');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/post_skill`, {
            method: 'POST',
            headers: { "Authorization": `Bearer ${accessToken}` },
            body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 409) {
            return { conflict: data.name_conflict || { name: novoNome, owned_by_user: false } };
        }

        if (!response.ok) {
            setAlert({
                type: 'error',
                title: t('skills_manager_add_error_title'),
                message: translateBackendError(data.error) || t('skills_manager_add_error_title'),
            });
            return { erro: true };
        }

        setSkillsByTab((prev) => ({
            ...prev,
            minhas: [data, ...(prev.minhas || []).filter((s) => s.skill_id !== data.skill_id)],
        }));
        setActiveTab('minhas');
        return { ok: true };
    };

    // Tarefa 3.3 (FR-013): le o arquivo escolhido em "Adicionar Skill" e
    // bloqueia o envio ao backend por extensao/nome/descricao fora do
    // permitido, sem nenhuma chamada de rede nesse caso. `[REVISADO na Tarefa
    // 11.2, FR-074]`: o bloqueio por tamanho (FR-052) foi removido.
    // Extraído (achado da auditoria retroativa, specs/skills/plan.md, 2026-09-15) pra ser
    // reaproveitado tanto pelo `<input type="file">` (clique) quanto pelo drag-and-drop no
    // ActionCard - mesma validação/fluxo de conflito de nome pros dois caminhos.
    const processAddSkillFile = async (file) => {
        if (!file) return;

        setAddingSkill(true);
        try {
            // getSkillFileSizes so' precisa rodar aqui pelo efeito colateral
            // de levantar UnsupportedSkillFileError pra um formato de
            // arquivo (avulso) que nao seja .md/.zip/.skill - a allow-list de
            // extensoes dos arquivos DENTRO do upload (FR-054) foi removida
            // pela FR-075 (Tarefa 11.3).
            await getSkillFileSizes(file);

            // FR-030 (Tarefa 5.7): mesmo limite de nome do skill-creator
            // tambem bloqueia o upload ANTES do envio, no mesmo padrao do
            // limite de conteudo (FR-013) acima.
            const skillName = await extractSkillFileName(file);
            if (skillName && skillName.length > SKILL_MAX_NAME_CHARS) {
                setAlert({
                    type: 'error',
                    title: t('skills_manager_name_limit_exceeded_title'),
                    message: t('skills_manager_name_limit_exceeded_message', {
                        chars: skillName.length,
                        limit: SKILL_MAX_NAME_CHARS,
                    }),
                });
                return;
            }

            // Tarefa 11.1 (FR-077): mesmo padrao do limite de nome acima.
            const skillDescription = await extractSkillFileDescription(file);
            if (skillDescription && skillDescription.length > SKILL_MAX_DESCRIPTION_CHARS) {
                setAlert({
                    type: 'error',
                    title: t('skills_manager_description_limit_exceeded_title'),
                    message: t('skills_manager_description_limit_exceeded_message', {
                        chars: skillDescription.length,
                        limit: SKILL_MAX_DESCRIPTION_CHARS,
                    }),
                });
                return;
            }

            // Tarefa 7.5 (FR-050): nome ja usado por uma skill PROPRIA
            // existente (cache da aba "minhas", Tarefa 3.1) - confirma antes
            // de enviar, em vez de deixar create_skill substituir em
            // silencio (delega para update_skill quando dono e nome batem,
            // Tarefa 0.1) sem o usuario saber que estava sobrescrevendo. Se a
            // aba "minhas" ainda nao foi buscada (raro - e' a aba padrao ao
            // abrir o modal), a checagem so' nao acontece aqui; o backend
            // continua resolvendo a mesma substituicao normalmente.
            if (skillName) {
                const existing = (skillsByTab.minhas || []).find((s) => s.name === skillName);
                if (existing) {
                    setNameConflictError('');
                    setNameConflict({
                        file, name: existing.name, owned_by_user: true, skill_id: existing.skill_id,
                    });
                    return;
                }
            }

            // Conflito que a pre-checagem acima nao pega (nome de OUTRO dono,
            // ou aba "minhas" ainda nao carregada) chega como 409 do backend
            // e abre o mesmo popup - com "Substituir" ou sem ele, conforme
            // `owned_by_user`.
            const resultado = await submitSkillFile(file);
            if (resultado?.conflict) {
                setNameConflictError('');
                setNameConflict({ file, ...resultado.conflict });
            }
        } catch (error) {
            const message = error instanceof UnsupportedSkillFileError
                ? t('skills_manager_add_unsupported_format')
                : t('skills_manager_add_error_title');
            setAlert({ type: 'error', title: t('skills_manager_add_error_title'), message });
        } finally {
            setAddingSkill(false);
        }
    };

    const handleAddSkillFileChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        processAddSkillFile(file);
    };

    const handleCancelNameConflict = () => {
        setNameConflict(null);
        setNameConflictError('');
    };

    // Caminho comum das duas saidas do popup: reenvia o MESMO arquivo,
    // mudando so a instrucao (substituir a skill existente x criar uma nova
    // com outro nome). Um 409 de novo - o nome escolhido tambem estava
    // ocupado - mantem o popup aberto com o aviso, em vez de fechar tudo e
    // fazer o usuario recomecar o upload.
    const reenviarSkillEmConflito = async (opcoes, nomeTentado) => {
        const pendente = nameConflict;
        if (!pendente) return;

        setAddingSkill(true);
        try {
            const resultado = await submitSkillFile(pendente.file, opcoes);
            if (resultado?.conflict) {
                setNameConflict({ file: pendente.file, ...resultado.conflict });
                setNameConflictError(t('skills_manager_name_conflict_taken', {
                    name: nomeTentado || pendente.name,
                }));
                return;
            }
            handleCancelNameConflict();
        } catch (error) {
            handleCancelNameConflict();
            setAlert({
                type: 'error',
                title: t('skills_manager_add_error_title'),
                message: t('skills_manager_add_error_title'),
            });
        } finally {
            setAddingSkill(false);
        }
    };

    const handleReplaceConflictingSkill = () => reenviarSkillEmConflito({ substituir: true });

    const handleCreateWithNewName = (novoNome) => reenviarSkillEmConflito({ novoNome }, novoNome);

    return (
        <div
            className="modal-overlay-skills"
            onClick={handleOverlayClick}
            data-testid="skills-manager-overlay"
        >
            <div
                className="modal-content-skills"
                data-testid="skills-manager-modal"
                // Sem isso, soltar o arquivo por acidente fora da área exata do ActionCard
                // (mas ainda dentro do modal) faria o navegador tentar abrir/navegar pro
                // arquivo em vez de simplesmente ignorar o drop.
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => event.preventDefault()}
            >
                {detailSkill ? (
                    <SkillDetailView
                        skill={detailSkill}
                        onBack={() => setDetailSkill(null)}
                        onShare={() => setShareSkillTarget(detailSkill)}
                        onDownload={() => handleDownloadSkill(detailSkill)}
                        onTryInChat={() => handleSelectSkill(detailSkill)}
                        onEditInChat={() => handleEditSkill(detailSkill)}
                        onActiveChanged={handleSkillActiveChanged}
                        onSkillUpdated={handleSkillEdited}
                        onDeleted={handleSkillDeleted}
                    />
                ) : (
                    <>
                        <div className="skills-manager-header">
                            <p className="skills-manager-title">
                                {t('skills_manager_title')}
                            </p>
                            <button
                                type="button"
                                className="skills-manager-close"
                                aria-label={t('closeIcon')}
                                onClick={onClose}
                                data-testid="skills-manager-close"
                            >
                                <img src="/imagens/x.svg" alt="" />
                            </button>
                        </div>

                        <div className="skills-manager-toolbar">
                            <div className="skills-manager-search">
                                <img src="/imagens/search.svg" alt="" className="skills-manager-search-icon" />
                                <input
                                    type="text"
                                    className="skills-manager-search-input"
                                    placeholder={t('skills_manager_search_placeholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    data-testid="skills-manager-search-input"
                                />
                            </div>

                            <div className="skills-manager-tabs">
                                {TABS.map((tab) => (
                                    <button
                                        type="button"
                                        key={tab.id}
                                        className={`skills-manager-tab ${activeTab === tab.id ? 'skills-manager-tab--active' : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                        data-testid={`skills-manager-tab-${tab.id}`}
                                    >
                                        {t(tab.labelKey)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="skills-manager-body">
                            {/* ActionCard fica fixo independente da busca de skills (GET
                                /get_skills, Tarefa 3.1) - "Adicionar Skill"/"Criar com Iris"
                                nao sao dados da rota, so o spinner/lista abaixo dependem dela. */}
                            <div className="skills-manager-cards" data-testid="skills-manager-cards">
                                <ActionCard
                                    onAddSkill={() => addSkillInputRef.current?.click()}
                                    addingSkill={addingSkill}
                                    onCreateWithIris={handleCreateWithIris}
                                    onDropFile={processAddSkillFile}
                                />
                                {loading ? (
                                    <div className="skills-manager-loading" data-testid="skills-manager-loading">
                                        <CircularProgress size={32} thickness={4} />
                                    </div>
                                ) : skillsErrorByTab[activeTab] ? (
                                    <div className="skills-manager-error" data-testid="skills-manager-error">
                                        <p className="skills-manager-error-message">
                                            {t('skills_manager_load_error_message')}
                                        </p>
                                        <button
                                            type="button"
                                            className="skills-manager-error-retry"
                                            data-testid="skills-manager-retry"
                                            onClick={handleRetryTab}
                                        >
                                            {t('skills_manager_load_error_retry')}
                                        </button>
                                    </div>
                                ) : (
                                    filteredSkills.map((skill) => (
                                        <SkillCard
                                            key={skill.skill_id}
                                            skill={skill}
                                            onSelect={() => setDetailSkill(skill)}
                                            onEdit={() => setDetailSkill(skill)}
                                            onShare={() => setShareSkillTarget(skill)}
                                            showEditButton={activeTab === 'minhas'}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    </>
                )}

                <input
                    type="file"
                    accept=".md,.zip,.skill"
                    ref={addSkillInputRef}
                    onChange={handleAddSkillFileChange}
                    style={{ display: 'none' }}
                    data-testid="skills-manager-add-file-input"
                />
            </div>

            {alert && (
                <AlertsCustom
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}

            <ShareSkillModal
                isOpen={shareSkillTarget !== null}
                onClose={() => setShareSkillTarget(null)}
                skill={shareSkillTarget}
                onUpdated={handleShareUpdated}
                onError={(message) => setAlert({
                    type: 'error',
                    title: t('skills_share_error_title'),
                    message,
                })}
            />

            <SkillNameConflictDialog
                isOpen={nameConflict !== null}
                conflict={nameConflict}
                maxNameChars={SKILL_MAX_NAME_CHARS}
                busy={addingSkill}
                errorMessage={nameConflictError}
                onReplace={handleReplaceConflictingSkill}
                onCreateWithName={handleCreateWithNewName}
                onCancel={handleCancelNameConflict}
            />
        </div>
    );
}

export default SkillsManagerModal;
